import dayjs from 'dayjs';
import { mergeWith } from 'lodash-es';
import { z } from 'zod';
import type defaultConfigFile from './default.json';
import type defaultProdConfigFile from './default.prod.json';

const UrlMatchItemSchema = z.object({
	at: z.string(),
	when: z.string(),
	then: z.string(),
});

const PageConfigSchema = z.object({
	pageEdge: z.record(z.string(), z.array(z.string())).describe('页面的边链接关系'),
	urlMatch: z.array(UrlMatchItemSchema).describe('路由落地默认匹配到哪个页面'),
});

const RawPageConfigSchema = z.object({
	pageEdge: z.record(z.string(), z.array(z.string())).optional().describe('页面的边链接关系'),
	urlMatch: z.array(UrlMatchItemSchema).optional().describe('路由落地默认匹配到哪个页面'),
});

const RawAppSpecConfig = z
	.object({
		defaultGreenLabel: z.string().describe('默认绿色标签'),
		isHiddenCreatorEntrance: z.boolean().nullish().describe('是否隐藏 Creator 入口'),
		showChatbotSince: z
			.string()
			.refine((val) => val === '' || dayjs(val, 'YYYY-MM-DD HH:mm:ss', true).isValid(), {
				message: 'Invalid ISO date',
			})
			.describe('聊天机器人显示时间'),
		googleChatbotDisabled: z.boolean().optional().describe('是否对谷歌渠道关闭 chatbot'),
		metaShowChatbot: z.boolean().optional().describe('meta 渠道是否展示 chatbot'),
		showChatbotUserType: z.array(z.number()).optional().describe('允许展示 chatbot 的用户类型'),
		FacebookAppInfo: z
			.object({
				EnableFacebookRemoteConfig: z.boolean(),
				FacebookAppId: z.string(),
				FacebookClientToken: z.string(),
				FacebookConfigVersion: z.number(),
			})
			.describe('链接到的fb帐号'),
		pageConfigPreset: z.string().optional().describe('页面路由配置模板'),
		pageConfig: RawPageConfigSchema.optional().describe('页面路由配置'),
	})
	.passthrough();

const AppSpecConfig = RawAppSpecConfig.omit({
	pageConfigPreset: true,
	pageConfig: true,
})
	.extend({
		pageConfig: PageConfigSchema.describe('页面路由配置'),
	})
	.passthrough();

const RawAppSpecMapSchema = z
	.object({
		a1: RawAppSpecConfig,
		minimix: RawAppSpecConfig,
	})
	.catchall(RawAppSpecConfig);

const AppSpecMapSchema = z
	.object({
		a1: AppSpecConfig,
		minimix: AppSpecConfig,
	})
	.catchall(AppSpecConfig);

const RawConfigDataSchema = z
	.object({
		EmergencyDisableRiskContent: z.boolean().describe('紧急拉闸到绿色包'),
		LANG_URL_MAP: z.record(z.string(), z.string()).describe('支持的语言代码'),
		LANG_SERVER_MAP: z.record(z.string(), z.string()).describe('前端语言代码对应的后端语言代码'),
		specLabels: z
			.object({
				video: z.array(z.string()),
			})
			.describe('视频相关标签'),
		pageConfig: z
			.object({
				pageNode: z.array(z.string()).describe('页节点：Tab'),
				pageEnum: z.array(z.string()).describe('具体的页面'),
			})
			.describe('所有a1支持的页面和页面节点名字定义，方便类型检查'),
		pageConfigPresets: z
			.record(z.string(), PageConfigSchema)
			.optional()
			.describe('本地 pageConfig 默认模板'),
		appSpec: RawAppSpecMapSchema.describe('每个矩阵特定的配置'),
		iosSubscriptionIds: z.array(z.string()).describe('ios订阅套餐id'),
		whatsappInfo: z
			.object({
				createAssistantNumber: z.string(),
				feedbackNumber: z.string(),
			})
			.describe('站内 whatapp 链接'),
		feedback: z
			.object({
				feedbackQuestionnaire: z.string(),
				creatorCenter: z.string(),
			})
			.describe('站内 feedback 链接'),
		AndroidSubscriptionIds: z.array(z.string()).describe('android订阅套餐id'),
		AndroidOneTimeProductIds: z.array(z.string()),
		weekPriceExperimentKey: z.string().describe('不知道什么实验'),
		svipSubscribe: z
			.record(z.string(), z.string())
			.optional()
			.describe(
				'SVIP默认订阅key配置，支持按国家覆盖，如 week/week_{countryId}/year/year_{countryId}',
			),
		aicr: z
			.object({
				model: z.string(),
			})
			.describe('前端Review使用的模型'),
	})
	.passthrough();

const ConfigDataSchema = RawConfigDataSchema.omit({
	pageConfigPresets: true,
})
	.extend({
		appSpec: AppSpecMapSchema.describe('每个矩阵特定的配置'),
	})
	.passthrough();

type PageConfig = z.infer<typeof PageConfigSchema>;
type RawPageConfig = z.infer<typeof RawPageConfigSchema>;
type RawAppSpecConfigType = z.infer<typeof RawAppSpecConfig>;
type AppSpecConfigType = z.infer<typeof AppSpecConfig>;
export type ConfigAppName =
	| keyof (typeof defaultConfigFile)['data']['appSpec']
	| keyof (typeof defaultProdConfigFile)['data']['appSpec'];

const mergePageConfig = (preset?: PageConfig, override?: RawPageConfig) =>
	mergeWith({}, preset ?? {}, override ?? {}, (_objValue, srcValue) => {
		if (Array.isArray(srcValue)) {
			return srcValue;
		}

		return undefined;
	});

const resolveAppPageConfig = (
	appConfig: RawAppSpecConfigType,
	pageConfigPresets?: RawConfigData['pageConfigPresets'],
) => {
	const presetPageConfig = appConfig.pageConfigPreset
		? pageConfigPresets?.[appConfig.pageConfigPreset]
		: undefined;

	return PageConfigSchema.parse(mergePageConfig(presetPageConfig, appConfig.pageConfig));
};

export const resolveConfigData = (data: RawConfigData): ConfigData => {
	const { pageConfigPresets, appSpec, ...restData } = data;
	const resolvedAppSpec = Object.fromEntries(
		Object.entries(appSpec).map(([appName, appConfig]) => {
			const {
				pageConfigPreset: _pageConfigPreset,
				pageConfig: _pageConfig,
				...restAppConfig
			} = appConfig;

			return [
				appName,
				{
					...restAppConfig,
					pageConfig: resolveAppPageConfig(appConfig, pageConfigPresets),
				},
			];
		}),
	) as ConfigData['appSpec'];

	return {
		...restData,
		appSpec: resolvedAppSpec,
	};
};

let RawConfigDataResponseSchema = z.object({
	data: RawConfigDataSchema,
});

RawConfigDataResponseSchema = RawConfigDataResponseSchema.superRefine(({ data }, ctx) => {
	const { pageConfig, pageConfigPresets, appSpec } = data;
	const pageNodeSet = new Set(pageConfig.pageNode);
	const allPageSet = new Set([...pageConfig.pageNode, ...pageConfig.pageEnum]);

	if (pageConfig.pageNode.length + pageConfig.pageEnum.length !== allPageSet.size) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'pageNode and pageEnum must not have overlapping values',
			path: ['pageConfig'],
		});
	}

	for (const [appName, appConfig] of Object.entries(appSpec)) {
		if (appConfig.pageConfigPreset && !pageConfigPresets?.[appConfig.pageConfigPreset]) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `pageConfigPreset "${appConfig.pageConfigPreset}" must exist in pageConfigPresets`,
				path: ['appSpec', appName, 'pageConfigPreset'],
			});
			continue;
		}

		const resolvedPageConfig = PageConfigSchema.safeParse(
			mergePageConfig(
				appConfig.pageConfigPreset ? pageConfigPresets?.[appConfig.pageConfigPreset] : undefined,
				appConfig.pageConfig,
			),
		);

		if (!resolvedPageConfig.success) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `pageConfig for "${appName}" must resolve to a complete config`,
				path: ['appSpec', appName, 'pageConfig'],
			});
			continue;
		}

		const { pageEdge, urlMatch } = resolvedPageConfig.data;

		for (const [fromNode, toNodes] of Object.entries(pageEdge)) {
			if (!pageNodeSet.has(fromNode)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `pageEdge key 「${fromNode}」 must exist in pageNode`,
					path: ['appSpec', appName, 'pageConfig', 'pageEdge', fromNode],
				});
			}

			for (const toNode of toNodes) {
				if (!allPageSet.has(toNode)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: `pageEdge value 「${toNode}」 must exist in pageNode or pageEnum`,
						path: ['appSpec', appName, 'pageConfig', 'pageEdge', fromNode],
					});
				}
			}
		}

		for (const urlMatchItem of urlMatch) {
			if (!pageNodeSet.has(urlMatchItem.at)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `urlMatch.at 「${urlMatchItem.at}」 must exist in pageNode`,
					path: ['appSpec', appName, 'pageConfig', 'urlMatch'],
				});
			}

			if (!allPageSet.has(urlMatchItem.then)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `urlMatch.then "${urlMatchItem.then}" must exist in pageNode or pageEnum`,
					path: ['appSpec', appName, 'pageConfig', 'urlMatch'],
				});
			}
		}
	}
});

const ResolvedConfigDataResponseSchema = z.object({
	data: ConfigDataSchema,
});

export type RawConfigDataResponse = z.infer<typeof RawConfigDataResponseSchema>;
export type RawConfigData = RawConfigDataResponse['data'];
type ResolvedConfigDataResponse = z.infer<typeof ResolvedConfigDataResponseSchema>;
export type ConfigData = ResolvedConfigDataResponse['data'] & {
	appSpec: Record<ConfigAppName, AppSpecConfigType>;
};
export type ConfigDataResponse = ResolvedConfigDataResponse & {
	data: ConfigData;
};
const ConfigDataResponseSchema = RawConfigDataResponseSchema;

export {
	ConfigDataResponseSchema,
	PageConfigSchema,
	RawConfigDataResponseSchema,
	ResolvedConfigDataResponseSchema,
};
